// View-it: the video twin of Hear-it. A red, slow-pulsing dot + a "View it" button that opens the edited video in a lightbox.
// Placement: Video Desk → "Place It" → navigate anywhere → "Drop here" → tap the spot → "Add it here?" → done.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Play, X, Film } from "lucide-react";

const PENDING_KEY = "wall_place_video";
const src = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const fmt = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "");

export const readPendingPlacement = () => { try { return JSON.parse(sessionStorage.getItem(PENDING_KEY) || "null"); } catch { return null; } };
export const setPendingPlacement = (p) => { if (p) sessionStorage.setItem(PENDING_KEY, JSON.stringify(p)); else sessionStorage.removeItem(PENDING_KEY); window.dispatchEvent(new CustomEvent("wall:place-video")); };

// Lightbox player — pauses the music first, closes on Esc / backdrop.
export const ViewItModal = ({ media, title, onClose }) => {
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("wall:pause-music"));
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[1200] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="view-it-modal">
      <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-2 text-white">
          <span className="h-2.5 w-2.5 rounded-full bg-[#FF3B3B] animate-[pulse_2.6s_ease-in-out_infinite]" />
          <p className="m-0 text-sm font-black uppercase tracking-[0.18em]">View it</p>
          <p className="m-0 text-sm text-white/70 truncate">{title || media.title || "Video"}</p>
          <button type="button" onClick={onClose} className="ml-auto rounded-full p-1.5 bg-white/10 hover:bg-white/20" aria-label="Close video" data-testid="view-it-modal-close"><X className="h-5 w-5" /></button>
        </div>
        {media.type === "embed"
          ? <iframe src={media.embed_url} title={media.title || "Video"} className="w-full aspect-video rounded-2xl bg-black" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
          : <video controls autoPlay playsInline className="w-full aspect-video rounded-2xl bg-black" src={src(media.url)} data-testid="view-it-video" />}
      </div>
    </div>,
    document.body
  );
};

// The button that sits on the page (inside the pin card).
export const ViewItButton = ({ pin }) => {
  const [open, setOpen] = useState(false);
  const m = pin.media || {};
  return (
    <>
      {pin.label && <p className="m-0 mb-1 text-[11px] font-bold text-[#1A1A1A] max-w-[14rem] leading-tight">{pin.label}</p>}
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-[#FF3B3B] text-white pl-2 pr-3.5 py-1.5 text-xs font-black shadow-md hover:scale-[1.03] active:scale-95 transition-transform" data-testid={`view-it-${pin.pin_id}`}>
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/20"><Play className="h-3.5 w-3.5 ml-0.5" /></span>
        View it{m.duration_sec ? <span className="font-normal opacity-80">· {fmt(m.duration_sec)}</span> : null}
      </button>
      <p className="m-0 mt-1 text-[10px] text-[#5C5C5C] truncate max-w-[14rem] inline-flex items-center gap-1"><Film className="h-3 w-3" /> {m.title || "Video"}</p>
      {open && <ViewItModal media={m} title={pin.label} onClose={() => setOpen(false)} />}
    </>
  );
};
